
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EditCourseToolbar from './EditCourseToolbar';
import CourseDetailsTab from './CourseDetailsTab';
import CourseModulesTab from './CourseModulesTab';
import EditModule from './EditModule';
import EditLesson from './EditLesson';
import { useCourses } from '../../providers/CourseProvider';
import { OperationRequest } from '@peercolab/engine';
import { Session } from '../../peer-colab-model/Session';
import { useToast } from '../../providers/ToastProvider';
import { onRevert } from '../../utils/GeneralHelpers';
import { CourseManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';

interface EditCourseProps {
  courseId: string
  onBack: () => void;
}

function EditCourse({ courseId, onBack }: EditCourseProps) {
  const { onError } = useToast();
  const { courses, loadCourse } = useCourses()
  const [tab, setTab] = useState(0);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [editingLesson, setEditingLesson] = useState<string | null>(null);

  useEffect(() => {
    const initCourse = async () => {
      (await loadCourse(courseId))
    }
    initCourse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const course = courses.find(c => c.id === courseId);
  if (!course) {
    return null;
  }

  const module = editingModuleId ?
    course.modules.find(m => m.module.id === editingModuleId)?.module :
    course.modules.find(m => m.module.lessons.some(l => l.lesson.id === editingLesson))?.module;
  const lesson = module?.lessons.find(l => l.lesson.id === editingLesson)?.lesson;

  const patchField = async <R,V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void): Promise<void> => {
    if (newValue === currentValue) return
    const client = lesson ?
      Session.getLessonClient(course.id, lesson.id) :
      Session.getCourseClient(course.id)
    onError(onRevert((await client.request(operation)), () => revertHandler(currentValue)))
      .maybeOk(() => {
        loadCourse(course.id)
        revertHandler(newValue);
      });
  }

  // Handler to go back to modules list from editing
  const handleBackToModules = () => setEditingModuleId(null);

  // Handler to start editing a module
  const handleEditModule = (moduleId: string) => setEditingModuleId(moduleId);

  // Handler to start editing a lesson from a module
  const handleEditLesson = (lessonId: string | null) => {
    setEditingLesson(lessonId);
  };

  // Handler to go back to module from lesson
  const handleBackToModule = () => setEditingLesson(null);

  let toolbarOnModuleClick = undefined;
  let toolbarOnCourseClick = undefined;
  if (editingLesson) {
    toolbarOnModuleClick = handleBackToModule;
    toolbarOnCourseClick = editingModuleId !== null ? handleBackToModules : undefined;
  } else if (editingModuleId !== null) {
    toolbarOnCourseClick = handleBackToModules;
  }

  return (
    <Box>
      <EditCourseToolbar
        course={course}
        module={module}
        lesson={lesson}
        onBack={onBack}
        onCourseClick={toolbarOnCourseClick}
        onModuleClick={toolbarOnModuleClick}
        onAICompletion={() => loadCourse(course.id)}
      />
      <Box sx={{ p: 3 }}>
        {module && lesson ? (
          <EditLesson
            course={course}
            module={module}
            lesson={lesson}
            patchField={patchField}
            onBack={handleBackToModule}
          />
        ) : !module ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
                <Tab label="Course Details" />
                <Tab label="Modules" />
              </Tabs>
              <Box sx={{ ml: 2 }}>
                <PublishStatusComponent
                  status={course.state}
                  onTransition={async (action) => {
                    const result = await Session.getCourseClient(course.id).request(CourseManagment.updateCourseState({ stateTransition: action }));  
                    (await loadCourse(courseId))
                    return result;
                  }}
                />
              </Box>
            </Box>
            {tab === 0 && (
              <CourseDetailsTab course={course} patchField={patchField} />
            )}
            {tab === 1 && (
              <CourseModulesTab
                course={course}
                patchField={patchField}
                onEditModule={handleEditModule}
              />
            )}
          </>
        ) : module && (
          <EditModule
            module={module}
            patchField={patchField}
            course={course}
            onBackToCourse={handleBackToModules}
            onEditLesson={lessonId => handleEditLesson(lessonId)}
          />
        )}
      </Box>
    </Box>
  );
}

export default EditCourse;
